import {
  publicPlatformStatusSchema,
  type PublicPlatformStatusView,
} from "@digital-signage/contracts";

const CACHE_KEY = "digital-screen:last-status:v1";
const REQUEST_TIMEOUT_MS = 5_000;

export type StatusFeedSource = "bundled" | "cached" | "live";

export interface LoadedStatusFeed {
  source: StatusFeedSource;
  status: PublicPlatformStatusView;
}

function readCachedStatus(): PublicPlatformStatusView | null {
  try {
    const value = localStorage.getItem(CACHE_KEY);
    if (!value) return null;
    const parsed: unknown = JSON.parse(value);
    const status = publicPlatformStatusSchema.safeParse(parsed);
    return status.success ? status.data : null;
  } catch {
    return null;
  }
}

function cacheStatus(status: PublicPlatformStatusView): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(status));
  } catch {
    // A blocked storage API must not prevent the status page from rendering.
  }
}

async function fetchParsedStatus(
  path: string,
  signal?: AbortSignal,
): Promise<PublicPlatformStatusView> {
  const response = await fetch(path, {
    cache: "no-store",
    credentials: "omit",
    headers: { Accept: "application/json" },
    redirect: "error",
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) throw new Error("Status feed unavailable");
  const body: unknown = await response.json();
  return publicPlatformStatusSchema.parse(body);
}

export async function loadStatusFeed(): Promise<LoadedStatusFeed> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const status = await fetchParsedStatus("/api/status", controller.signal);
    cacheStatus(status);
    return { source: "live", status };
  } catch {
    const cached = readCachedStatus();
    if (cached) return { source: "cached", status: cached };
    const status = await fetchParsedStatus("/last-status.json");
    return { source: "bundled", status };
  } finally {
    window.clearTimeout(timeout);
  }
}
